import { useEffect, useState, useCallback } from "react";
import { 
  collection, 
  getDocs, 
  query, 
  where, 
  orderBy, 
  onSnapshot,
  doc,
  updateDoc 
} from "firebase/firestore";
import { db } from "@/lib/firebase";
import { useAuth } from "@/context/AuthContext";
import { 
  ChevronDown, 
  ChevronUp, 
  Copy, 
  Edit2, 
  Minus, 
  Plus,
  Circle,
  RefreshCw,
  Loader2
} from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import ReactMarkdown from "react-markdown";

// Types
interface Recording {
  id: string;
  briefTranscription?: string;
  detailedTranscription?: string;
  last40SecTranscription?: string;
  timestamp: Date;
}

interface Meeting {
  id: string;
  userId: string;
  name?: string;
  startTime: Date;
  endTime: Date;
  durationInSeconds: number;
  recordingCount: number;
  meetingNotes?: string;
}

enum TranscriptionViewType {
  brief = 'brief',
  detailed = 'detailed',
  last40Seconds = 'last40Seconds'
}

enum MeetingTab {
  yourNotes = 'yourNotes',
  aiNotes = 'aiNotes'
}

const UserDataSection = () => {
  const { user } = useAuth();
  
  // State
  const [meetings, setMeetings] = useState<Meeting[]>([]);
  const [groupedMeetings, setGroupedMeetings] = useState<Record<string, Meeting[]>>({});
  const [loading, setLoading] = useState(false);
  const [expandedMeetings, setExpandedMeetings] = useState<Record<string, boolean>>({});
  const [meetingRecordings, setMeetingRecordings] = useState<Record<string, Recording[]>>({});
  const [loadingRecordings, setLoadingRecordings] = useState<Record<string, boolean>>({});
  const [transcriptionViewTypes, setTranscriptionViewTypes] = useState<Record<string, TranscriptionViewType>>({});
  const [selectedTabs, setSelectedTabs] = useState<Record<string, MeetingTab>>({});
  
  // Meeting name editing state
  const [editingMeetingName, setEditingMeetingName] = useState<Record<string, boolean>>({});
  const [editingMeetingNameValue, setEditingMeetingNameValue] = useState<string>("");
  const [editingMeetingId, setEditingMeetingId] = useState<string | null>(null);

  const formatTime = (dateTime: Date) => {
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const dateTimeDay = new Date(dateTime.getFullYear(), dateTime.getMonth(), dateTime.getDate());
    
    if (dateTimeDay.getTime() === today.getTime()) {
      return dateTime.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
    } else if (dateTime > new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000)) {
      return dateTime.toLocaleDateString('en-US', { weekday: 'short' });
    } else {
      return dateTime.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    }
  };

  const groupMeetingsByDate = (meetings: Meeting[]) => {
    const grouped: Record<string, Meeting[]> = {};
    
    for (const meeting of meetings) {
      const date = meeting.startTime;
      const today = new Date();
      const yesterday = new Date(today);
      yesterday.setDate(yesterday.getDate() - 1);
      
      let groupKey: string;
      const isToday = date.toDateString() === today.toDateString();
      const isYesterday = date.toDateString() === yesterday.toDateString();
      
      if (isToday) {
        groupKey = 'Today';
      } else if (isYesterday) {
        groupKey = 'Yesterday';
      } else {
        groupKey = date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
      }
      
      if (!grouped[groupKey]) {
        grouped[groupKey] = [];
      }
      grouped[groupKey].push(meeting);
    }

    // Sort meetings within each group by startTime (newest first)
    for (const key in grouped) {
      grouped[key].sort((a, b) => b.startTime.getTime() - a.startTime.getTime());
    }
    
    // Sort group keys
    const sortedKeys = Object.keys(grouped).sort((a, b) => {
      if (a === 'Today') return -1;
      if (b === 'Today') return 1;
      if (a === 'Yesterday') return -1;
      if (b === 'Yesterday') return 1;
      
      const dateA = new Date(a);
      const dateB = new Date(b);
      return dateB.getTime() - dateA.getTime();
    });
    
    const sortedGrouped: Record<string, Meeting[]> = {};
    for (const key of sortedKeys) {
      sortedGrouped[key] = grouped[key];
    }
    
    return sortedGrouped;
  };

  const loadRecordingsForMeeting = async (meetingId: string) => {
    if (!user) return;
    
    setLoadingRecordings(prev => ({ ...prev, [meetingId]: true }));

    try {
      const recordingsQuery = query(
        collection(db, 'vusers', user.uid, 'meetings', meetingId, 'recordings'),
        orderBy('timestamp', 'asc')
      );
      
      const snapshot = await getDocs(recordingsQuery);
      const recordings = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        timestamp: doc.data().timestamp?.toDate() || new Date()
      })) as Recording[];
      
      setMeetingRecordings(prev => ({ ...prev, [meetingId]: recordings }));
    } catch (error) {
      console.error('Failed to load recordings:', error);
    } finally {
      setLoadingRecordings(prev => ({ ...prev, [meetingId]: false }));
    }
  };

  const loadMeetings = useCallback(async () => {
    if (!user) return;
    
    setLoading(true);
    try {
      const meetingsQuery = query(
        collection(db, 'vusers', user.uid, 'meetings'),
        orderBy('startTime', 'desc')
      );
      
      const snapshot = await getDocs(meetingsQuery);
      const loadedMeetings = snapshot.docs.map(doc => {
        const data = doc.data();
        return {
          id: doc.id,
          userId: data.userId,
          name: data.name,
          startTime: data.startTime?.toDate() || new Date(),
          endTime: data.endTime?.toDate() || new Date(),
          durationInSeconds: data.durationInSeconds || 0,
          recordingCount: data.recordingCount || 0,
          meetingNotes: data.meetingNotes
        } as Meeting;
      }).filter(meeting => meeting.recordingCount >= 1);
      
      setMeetings(loadedMeetings);
      setGroupedMeetings(groupMeetingsByDate(loadedMeetings));
    } catch (error) {
      console.error('Failed to load meetings:', error);
      toast.error("Failed to load meetings. Please try again.");
    } finally {
      setLoading(false);
    }
  }, [user]);

  // Set up real-time listener
  useEffect(() => {
    if (!user) return;

    const unsubscribe = onSnapshot(
      query(
        collection(db, 'vusers', user.uid, 'meetings'),
        orderBy('startTime', 'desc')
      ),
      (snapshot) => {
        const loadedMeetings = snapshot.docs.map(doc => {
          const data = doc.data();
          return {
            id: doc.id,
            userId: data.userId,
            name: data.name,
            startTime: data.startTime?.toDate() || new Date(),
            endTime: data.endTime?.toDate() || new Date(),
            durationInSeconds: data.durationInSeconds || 0,
            recordingCount: data.recordingCount || 0,
            meetingNotes: data.meetingNotes
          } as Meeting;
        }).filter(meeting => meeting.recordingCount >= 1);
        
        setMeetings(loadedMeetings);
        setGroupedMeetings(groupMeetingsByDate(loadedMeetings));
      },
      (error) => {
        console.error('Real-time listener error:', error);
      }
    );

    return () => unsubscribe();
  }, [user]);

  const toggleMeeting = async (meetingId: string) => {
    const isExpanded = expandedMeetings[meetingId];
    
    setExpandedMeetings(prev => ({ ...prev, [meetingId]: !isExpanded }));
    
    if (!isExpanded) {
      // Set defaults when expanding
      setTranscriptionViewTypes(prev => ({ 
        ...prev, 
        [meetingId]: TranscriptionViewType.last40Seconds 
      }));
      setSelectedTabs(prev => ({ 
        ...prev, 
        [meetingId]: MeetingTab.yourNotes 
      }));
      
      await loadRecordingsForMeeting(meetingId);
    }
  };

  const updateTranscriptionViewType = (meetingId: string, change: 1 | -1) => {
    const types = [TranscriptionViewType.last40Seconds, TranscriptionViewType.brief, TranscriptionViewType.detailed];
    const currentType = transcriptionViewTypes[meetingId] || TranscriptionViewType.last40Seconds;
    const currentIndex = types.indexOf(currentType);
    const newIndex = Math.max(0, Math.min(types.length - 1, currentIndex + change));
    
    setTranscriptionViewTypes(prev => ({ ...prev, [meetingId]: types[newIndex] }));
  };

  const getTranscriptionContent = (recording: Recording, viewType: TranscriptionViewType) => {
    switch(viewType) {
      case TranscriptionViewType.detailed:
        return recording.detailedTranscription || recording.briefTranscription || '';
      case TranscriptionViewType.brief:
        return recording.briefTranscription || recording.detailedTranscription || '';
      case TranscriptionViewType.last40Seconds:
        return recording.last40SecTranscription || recording.briefTranscription || '';
      default:
        return recording.briefTranscription || recording.detailedTranscription || '';
    }
  };

  const copyYourNotes = async (meeting: Meeting, recordings: Recording[], viewType: TranscriptionViewType) => {
    if (recordings.length === 0) {
      toast.error("No notes available for this meeting.");
      return;
    }

    const notes = recordings
      .map(recording => getTranscriptionContent(recording, viewType))
      .filter(content => content.length > 0)
      .map(content => `• ${content}`)
      .join('\n\n');

    if (!notes) {
      toast.error("No notes available for this meeting.");
      return;
    }

    const meetingName = meeting.name || 'Meeting';
    const meetingDate = meeting.startTime.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric', 
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
    
    const content = `${meetingName} - ${meetingDate}\n\nYour Notes:\n\n${notes}`;
    
    try {
      await navigator.clipboard.writeText(content);
      toast.success("Notes copied to clipboard.");
    } catch (error) {
      toast.error("Failed to copy notes to clipboard.");
    }
  };

  const copyAiNotes = async (meeting: Meeting) => {
    if (!meeting.meetingNotes) {
      toast.error("No AI notes available for this meeting.");
      return;
    }

    const meetingName = meeting.name || 'Meeting';
    const meetingDate = meeting.startTime.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric', 
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
    
    const content = `${meetingName} - ${meetingDate}\n\nAI Enhanced Notes:\n\n${meeting.meetingNotes}`;
    
    try {
      await navigator.clipboard.writeText(content);
      toast.success("AI notes copied to clipboard.");
    } catch (error) {
      toast.error("Failed to copy AI notes to clipboard.");
    }
  };

  const startEditingMeetingName = (meetingId: string, currentName: string) => {
    setEditingMeetingId(meetingId);
    setEditingMeetingName(prev => ({ ...prev, [meetingId]: true }));
    setEditingMeetingNameValue(currentName || 'Meeting');
  };

  const saveEditingMeetingName = async (meetingId: string) => {
    if (!user || !editingMeetingNameValue.trim()) {
      cancelEditingMeetingName(meetingId);
      return;
    }

    try {
      await updateDoc(
        doc(db, 'vusers', user.uid, 'meetings', meetingId),
        { name: editingMeetingNameValue.trim() }
      );
      
      // Update local state
      setMeetings(prev => prev.map(meeting => 
        meeting.id === meetingId 
          ? { ...meeting, name: editingMeetingNameValue.trim() }
          : meeting
      ));
      
      toast.success("Meeting name updated successfully.");
    } catch (error) {
      console.error('Failed to update meeting name:', error);
      toast.error("Failed to update meeting name.");
    } finally {
      cancelEditingMeetingName(meetingId);
    }
  };

  const cancelEditingMeetingName = (meetingId: string) => {
    setEditingMeetingName(prev => ({ ...prev, [meetingId]: false }));
    setEditingMeetingId(null);
    setEditingMeetingNameValue("");
  };

  const getTranscriptionViewLabel = (viewType: TranscriptionViewType) => {
    switch(viewType) {
      case TranscriptionViewType.brief:
        return 'Brief';
      case TranscriptionViewType.last40Seconds:
        return 'Last 40s';
      case TranscriptionViewType.detailed:
        return 'Detailed';
    }
  };

  if (!user) return null;

  return (
    <section className="py-16 bg-background">
      <div className="max-w-4xl mx-auto px-4">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-3xl font-bold">Meeting History</h2>
          <Button
            variant="outline"
            size="sm"
            onClick={loadMeetings}
            disabled={loading}
          >
            {loading ? (
              <Loader2 className="h-4 w-4 animate-spin mr-2" />
            ) : (
              <RefreshCw className="h-4 w-4 mr-2" />
            )}
            Refresh
          </Button>
        </div>

        {loading && meetings.length === 0 ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin mr-3" />
            <p className="text-muted-foreground">Loading your meetings...</p>
          </div>
        ) : Object.keys(groupedMeetings).length === 0 ? (
          <div className="text-center py-12">
            <p className="text-muted-foreground text-lg">No meetings found</p>
            <p className="text-muted-foreground text-sm mt-2">Your recorded meetings will appear here</p>
          </div>
        ) : (
          <div className="space-y-8">
            {Object.entries(groupedMeetings).map(([dateGroup, meetings]) => (
              <div key={dateGroup} className="space-y-4">
                <h3 className={`text-lg font-bold uppercase tracking-wide ${
                  dateGroup === 'Today' ? 'text-primary' : 'text-muted-foreground'
                }`}>
                  {dateGroup}
                </h3>
                
                <div className="space-y-3">
                  {meetings.map((meeting) => (
                    <div
                      key={meeting.id}
                      className="bg-card border rounded-lg p-6 shadow-sm"
                    >
                      {/* Meeting Header */}
                      <div 
                        className="flex items-start justify-between cursor-pointer"
                        onClick={() => !editingMeetingName[meeting.id] && toggleMeeting(meeting.id)}
                      >
                        <div className="flex-1 min-w-0">
                          {editingMeetingName[meeting.id] ? (
                            <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
                              <Input
                                value={editingMeetingNameValue}
                                onChange={(e) => setEditingMeetingNameValue(e.target.value)}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter') {
                                    saveEditingMeetingName(meeting.id);
                                  } else if (e.key === 'Escape') {
                                    cancelEditingMeetingName(meeting.id);
                                  }
                                }}
                                onBlur={() => saveEditingMeetingName(meeting.id)}
                                className="text-base font-semibold"
                                maxLength={50}
                                autoFocus
                              />
                            </div>
                          ) : (
                            <div className="flex items-center gap-2">
                              <h4 className="text-base font-semibold truncate">
                                {meeting.name || 'Meeting'}
                              </h4>
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-6 w-6 p-0 opacity-60 hover:opacity-100"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  startEditingMeetingName(meeting.id, meeting.name || '');
                                }}
                              >
                                <Edit2 className="h-3 w-3" />
                              </Button>
                            </div>
                          )}
                          <p className="text-sm text-muted-foreground mt-1">
                            Started at {meeting.startTime.toLocaleTimeString('en-US', { 
                              hour: 'numeric', minute: '2-digit', hour12: true 
                            })}
                          </p>
                        </div>
                        
                        <div className="flex items-center gap-4 ml-4">
                          <span className="text-sm text-muted-foreground">
                            {formatTime(meeting.startTime)}
                          </span>
                          {expandedMeetings[meeting.id] ? (
                            <ChevronUp className="h-5 w-5 text-muted-foreground" />
                          ) : (
                            <ChevronDown className="h-5 w-5 text-muted-foreground" />
                          )}
                        </div>
                      </div>

                      {/* Expanded Content */}
                      {expandedMeetings[meeting.id] && (
                        <div className="mt-6 space-y-4">
                          {/* Tabs - only show if AI notes exist */}
                          {meeting.meetingNotes ? (
                            <Tabs 
                              value={selectedTabs[meeting.id] || MeetingTab.yourNotes}
                              onValueChange={(value) => 
                                setSelectedTabs(prev => ({ 
                                  ...prev, 
                                  [meeting.id]: value as MeetingTab 
                                }))
                              }
                            >
                              <TabsList className="grid w-full grid-cols-2">
                                <TabsTrigger value={MeetingTab.yourNotes}>Your Notes</TabsTrigger>
                                <TabsTrigger value={MeetingTab.aiNotes}>AI Enhanced</TabsTrigger>
                              </TabsList>
                              
                              <TabsContent value={MeetingTab.yourNotes} className="mt-4">
                                {/* Your Notes Content */}
                                <div className="space-y-4">
                                  {/* Transcription Level Controls */}
                                  <div className="flex items-center justify-between">
                                    <div className="flex items-center gap-2">
                                      <Button
                                        variant="outline"
                                        size="sm"
                                        onClick={() => updateTranscriptionViewType(meeting.id, -1)}
                                        disabled={transcriptionViewTypes[meeting.id] === TranscriptionViewType.last40Seconds}
                                      >
                                        <Minus className="h-3 w-3" />
                                      </Button>
                                      <Button
                                        variant="outline"
                                        size="sm"
                                        onClick={() => updateTranscriptionViewType(meeting.id, 1)}
                                        disabled={transcriptionViewTypes[meeting.id] === TranscriptionViewType.detailed}
                                      >
                                        <Plus className="h-3 w-3" />
                                      </Button>
                                    </div>
                                    <Button
                                      variant="outline"
                                      size="sm"
                                      onClick={() => copyYourNotes(
                                        meeting, 
                                        meetingRecordings[meeting.id] || [], 
                                        transcriptionViewTypes[meeting.id] || TranscriptionViewType.last40Seconds
                                      )}
                                    >
                                      <Copy className="h-3 w-3" />
                                    </Button>
                                  </div>

                                  {/* Recordings List */}
                                  {loadingRecordings[meeting.id] ? (
                                    <div className="flex items-center gap-3 py-4">
                                      <Loader2 className="h-4 w-4 animate-spin" />
                                      <p className="text-sm text-muted-foreground">Loading notes...</p>
                                    </div>
                                  ) : !meetingRecordings[meeting.id] || meetingRecordings[meeting.id].length === 0 ? (
                                    <p className="text-sm text-muted-foreground italic py-2">No notes available</p>
                                  ) : (
                                    <div className="space-y-3">
                                      {meetingRecordings[meeting.id].map((recording) => {
                                        const content = getTranscriptionContent(
                                          recording, 
                                          transcriptionViewTypes[meeting.id] || TranscriptionViewType.last40Seconds
                                        );
                                        
                                        if (!content) return null;
                                        
                                        return (
                                          <div key={recording.id} className="flex gap-3">
                                            <Circle className="h-2 w-2 mt-2 text-primary flex-shrink-0" />
                                            <p className="text-sm leading-relaxed">{content}</p>
                                          </div>
                                        );
                                      })}
                                    </div>
                                  )}
                                </div>
                              </TabsContent>
                              
                              <TabsContent value={MeetingTab.aiNotes} className="mt-4">
                                {/* AI Enhanced Content */}
                                <div className="space-y-4">
                                  <div className="flex justify-end">
                                    <Button
                                      variant="outline"
                                      size="sm"
                                      onClick={() => copyAiNotes(meeting)}
                                    >
                                      <Copy className="h-3 w-3" />
                                    </Button>
                                  </div>
                                  
                                  <div className="prose prose-sm max-w-none dark:prose-invert">
                                    <div className="text-sm leading-relaxed">
                                      <ReactMarkdown 
                                        components={{
                                          h1: ({ children }) => <h1 className="text-lg font-semibold mb-3 mt-4 first:mt-0">{children}</h1>,
                                          h2: ({ children }) => <h2 className="text-base font-semibold mb-2 mt-3">{children}</h2>,
                                          h3: ({ children }) => <h3 className="text-sm font-semibold mb-2 mt-3">{children}</h3>,
                                          p: ({ children }) => <p className="mb-2 last:mb-0">{children}</p>,
                                          ul: ({ children }) => <ul className="list-disc pl-4 mb-2 space-y-1">{children}</ul>,
                                          ol: ({ children }) => <ol className="list-decimal pl-4 mb-2 space-y-1">{children}</ol>,
                                          li: ({ children }) => <li className="text-sm">{children}</li>,
                                          strong: ({ children }) => <strong className="font-semibold">{children}</strong>,
                                          em: ({ children }) => <em className="italic">{children}</em>,
                                          code: ({ children }) => <code className="bg-muted px-1 py-0.5 rounded text-xs">{children}</code>,
                                          blockquote: ({ children }) => <blockquote className="border-l-2 border-muted-foreground/20 pl-3 italic text-muted-foreground">{children}</blockquote>,
                                        }}
                                      >
                                        {meeting.meetingNotes || ''}
                                      </ReactMarkdown>
                                    </div>
                                  </div>
                                </div>
                              </TabsContent>
                            </Tabs>
                          ) : (
                            /* Direct Your Notes content when no AI notes */
                            <div className="space-y-4">
                              <div className="flex items-center justify-between">
                                <div className="flex items-center gap-2">
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => updateTranscriptionViewType(meeting.id, -1)}
                                    disabled={transcriptionViewTypes[meeting.id] === TranscriptionViewType.last40Seconds}
                                  >
                                    <Minus className="h-3 w-3" />
                                  </Button>
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => updateTranscriptionViewType(meeting.id, 1)}
                                    disabled={transcriptionViewTypes[meeting.id] === TranscriptionViewType.detailed}
                                  >
                                    <Plus className="h-3 w-3" />
                                  </Button>
                                </div>
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => copyYourNotes(
                                    meeting, 
                                    meetingRecordings[meeting.id] || [], 
                                    transcriptionViewTypes[meeting.id] || TranscriptionViewType.last40Seconds
                                  )}
                                >
                                  <Copy className="h-3 w-3" />
                                </Button>
                              </div>

                              {loadingRecordings[meeting.id] ? (
                                <div className="flex items-center gap-3 py-4">
                                  <Loader2 className="h-4 w-4 animate-spin" />
                                  <p className="text-sm text-muted-foreground">Loading notes...</p>
                                </div>
                              ) : !meetingRecordings[meeting.id] || meetingRecordings[meeting.id].length === 0 ? (
                                <p className="text-sm text-muted-foreground italic py-2">No notes available</p>
                              ) : (
                                <div className="space-y-3">
                                  {meetingRecordings[meeting.id].map((recording) => {
                                    const content = getTranscriptionContent(
                                      recording, 
                                      transcriptionViewTypes[meeting.id] || TranscriptionViewType.last40Seconds
                                    );
                                    
                                    if (!content) return null;
                                    
                                    return (
                                      <div key={recording.id} className="flex gap-3">
                                        <Circle className="h-2 w-2 mt-2 text-primary flex-shrink-0" />
                                        <p className="text-sm leading-relaxed">{content}</p>
                                      </div>
                                    );
                                  })}
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </section>
  );
};

export default UserDataSection; 